import React, { useState } from 'react'

import { useForm } from 'react-hook-form'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react'

import {
  useAddCustomSummaryPrompt,
  useInterview,
} from '@/react-query/interview/interview'
import { AutoResizeTextarea } from '@/views/components/AutoResizeTextarea/AutoResizeTextarea'
import Card from '@/views/components/Card'

import { PromptTemplate } from './CustomPromptsLibrary'

type AddCustomSummaryPartProps = {
  setIsAddCustomSummaryPartShown: React.Dispatch<React.SetStateAction<boolean>>
  promptTemplate?: PromptTemplate
}

interface AddCustomSummaryPartForm {
  name: string
  text: string
}

export function AddCustomSummaryPart({
  setIsAddCustomSummaryPartShown,
  promptTemplate,
}: AddCustomSummaryPartProps) {
  const addCustomSummaryPart = useAddCustomSummaryPrompt()
  const [hasScoring, setHasScoring] = useState(
    promptTemplate?.hasScoring || false,
  )

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    register,
  } = useForm<AddCustomSummaryPartForm>({
    defaultValues: {
      name: promptTemplate?.name || '',
      text: promptTemplate?.text || '',
    },
  })

  async function onSubmit({ name, text }: AddCustomSummaryPartForm) {
    await addCustomSummaryPart.mutateAsync({
      name,
      text,
      hasScoring,
    })

    setIsAddCustomSummaryPartShown(false)
  }

  const interview = useInterview()
  const isIntake = interview.data?.type === 'INTAKE'

  return (
    <Card className="flex flex-col flex-grow px-[2.5rem] pt-[1.25rem] pb-[1.75rem]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Text as={'h2'} fontWeight="bold" fontSize="1.25rem">
          {isIntake ? 'New role brief topic' : 'New scorecard topic'}
        </Text>

        <Flex mt={5} gap={8} alignItems={'center'}>
          <FormControl isInvalid={Boolean(errors?.name)} maxW={'300px'}>
            <Input
              id="topic-name"
              placeholder={'Card title'}
              variant={'brand-solid-white'}
              {...register('name', {
                required: 'Card title is required',
              })}
            />

            <FormErrorMessage>
              {errors.name && errors.name.message}
            </FormErrorMessage>
          </FormControl>

          <Flex alignItems={'center'} gap={3} flexShrink={0}>
            <Switch
              id="add-scoring"
              isChecked={hasScoring}
              onChange={(event) => {
                setHasScoring(event.target.checked)
              }}
              size={'lg'}
              variant={'brand-black'}
            />
            <FormLabel
              htmlFor="add-scoring"
              cursor={'pointer'}
              mb={0}
              display={'inline-flex'}
              lineHeight={1}
            >
              Add scoring
            </FormLabel>
          </Flex>
        </Flex>

        <Tooltip
          rounded={'1.25rem'}
          py={2}
          px={4}
          bgColor={'blackAlpha.900'}
          label={
            'This is a draft prompt for our AI.' +
            '\n' +
            'Feel free to modify it as you like.'
          }
          isDisabled={!promptTemplate}
          placement={'top-end'}
        >
          <FormControl isInvalid={Boolean(errors?.text)} mt={5}>
            <AutoResizeTextarea
              px="1.25rem"
              py="0.65rem"
              rounded={'1.25rem'}
              flexGrow={1}
              variant={'brand-solid-white'}
              placeholder={
                'Please describe what would you like AI to report on here'
              }
              minRows={5}
              {...register('text', {
                required: 'Topic description is required',
              })}
              className="w-full"
            />

            <FormErrorMessage>
              {errors.text && errors.text.message}
            </FormErrorMessage>
          </FormControl>
        </Tooltip>

        <Flex mt={4} fontSize={'16px'} alignItems={'center'}>
          <Box
            fontSize={'12px'}
            fontWeight={700}
            rounded={'4px'}
            h={'20px'}
            w={'40px'}
            minW={'40px'}
            bgColor={'rgba(139, 119, 251, 1)'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            color={'white'}
          >
            Tip
          </Box>
          <Box ml={3}>
            Have a look at our{' '}
            <Link
              href={
                'https://hirehire.notion.site/hirehire-ai-prompting-guide-1279018d1060800cb47ff4c12b43fe4d'
              }
              rel="noreferrer"
              target="_blank"
              fontWeight={700}
              textDecoration={'underline'}
            >
              Prompting Guide
            </Link>{' '}
            for some inspiration
          </Box>
        </Flex>

        <Box mt={5}>
          <Button
            w={'fit-content'}
            variant={'brand-solid'}
            isLoading={isSubmitting}
            type="submit"
            isDisabled={!isValid}
          >
            Create
          </Button>
          <Button
            w={'fit-content'}
            variant={'brand-solid-white'}
            ml={3}
            onClick={() => {
              setIsAddCustomSummaryPartShown(false)
            }}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Card>
  )
}
